import { Node, Edge } from 'reactflow';
import { ParsedFlowItem, FlowchartData } from '../types';

// Expanded set of keywords to improve pattern recognition
const PROCESS_INDICATORS = [
  'i', 'we', 'then', 'next', 'first', 'second', 'third', 'fourth', 'finally', 'after', 'later',
  'following', 'subsequently', 'proceed', 'continue', 'go to', 'move to', 'start', 'begin',
  'create', 'make', 'perform', 'do', 'implement', 'execute', 'step'
];

const DECISION_INDICATORS = [
  'if', 'when', 'decide', 'check', 'determine', 'verify', 'otherwise', 'else',
  'condition', 'choice', 'question', 'evaluate', 'assess', 'test', 'consider'
];

const CONDITION_INDICATORS = [
  'if', 'when', 'in case', 'should', 'is', 'are', 'was', 'were',
  'has', 'have', 'can', 'could', 'will', 'would', 'may', 'might'
];

// Words to ignore/filter out for better parsing
const FILLER_WORDS = [
  'um', 'uh', 'like', 'so', 'you know', 'actually', 'basically', 'literally',
  'just', 'kind of', 'sort of', 'a bit', 'please', 'well', 'okay', 'now'
];

const generateId = (): string => `node_${Math.random().toString(36).slice(2, 9)}`;

// Utility function to capitalize first letter
const capitalizeFirstLetter = (text: string) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Memoization cache for parsed segments to improve real-time performance
const parsedSegmentsCache = new Map<string, ParsedFlowItem[]>();

// Clean transcript before parsing
const cleanTranscriptForParsing = (text: string): string => {
  if (!text) return '';
  
  // Convert to lowercase for better matching
  let cleaned = text.toLowerCase().trim();
  
  // Remove filler words
  FILLER_WORDS.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    cleaned = cleaned.replace(regex, '');
  });
  
  // Normalize spacing
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

// Check if a segment contains any of the indicator words
const containsAnyIndicator = (segment: string, indicators: string[]): boolean => {
  return indicators.some(indicator => {
    const words = segment.split(/\s+/);
    return words.includes(indicator) || segment.includes(` ${indicator} `) || segment.startsWith(`${indicator} `);
  });
};

// Parse text into flowchart items
export const parseVoiceInput = (text: string): ParsedFlowItem[] => {
  // Clean and normalize text
  const cleanedText = cleanTranscriptForParsing(text);
  const normalizedText = cleanedText.replace(/[,.;]/g, '').trim();
  
  // Check if we already parsed this exact text (performance optimization)
  if (parsedSegmentsCache.has(normalizedText)) {
    return parsedSegmentsCache.get(normalizedText)!;
  }
  
  // Better regex for splitting into logical segments
  // Split on: sentence-ending punctuation, common conjunctions, or numbered items
  const segments = normalizedText
    .split(/\s+(?:and|then|next|after that|afterwards|subsequently)\s+|\.|\n|(?:\d+\.)\s+/)
    .filter(Boolean)
    .map(segment => segment.trim())
    .filter(segment => segment.length > 1); // Filter out very short segments
  
  if (segments.length === 0) {
    const emptyResult: ParsedFlowItem[] = [];
    parsedSegmentsCache.set(normalizedText, emptyResult);
    return emptyResult;
  }
  
  const items: ParsedFlowItem[] = [];
  
  // Add start node
  const startNode: ParsedFlowItem = {
    type: 'start',
    text: 'Start',
    id: generateId(),
  };
  items.push(startNode);
  
  let lastNodeId = startNode.id;
  
  // Track the sequence of nodes to detect duplicates
  const processedSegments = new Set<string>();
  
  segments.forEach((segment, index) => {
    // Skip processing very short segments that are likely incomplete
    if (segment.split(' ').length < 2 && index === segments.length - 1) {
      // Skip very short final segment which might be incomplete
      return;
    }
    
    // Skip processing duplicate segments
    if (processedSegments.has(segment)) {
      return;
    }
    
    processedSegments.add(segment);
    
    // Determine the type of the segment
    let type: ParsedFlowItem['type'] = 'process';
    let conditionText = '';
    
    const isDecision = containsAnyIndicator(segment, DECISION_INDICATORS);
    
    if (isDecision) {
      type = 'decision';
      
      // Try to extract the condition
      for (const indicator of CONDITION_INDICATORS) {
        if (segment.includes(` ${indicator} `) || segment.startsWith(`${indicator} `)) {
          const parts = segment.split(new RegExp(`\\b${indicator}\\b`), 2);
          if (parts.length > 1) {
            conditionText = parts[1].trim();
            segment = segment.replace(indicator + ' ' + conditionText, '').trim();
            break;
          }
        }
      }
      
      // If no condition was found but this is a decision, use the segment as the condition
      if (!conditionText && type === 'decision') {
        conditionText = segment;
      }
    } else {
      // Check if it's a process node
      const isProcess = containsAnyIndicator(segment, PROCESS_INDICATORS);
      
      if (isProcess) {
        type = 'process';
      }
    }
    
    // Create node
    const nodeId = generateId();
    const node: ParsedFlowItem = {
      id: nodeId,
      type,
      text: segment,
    };
    
    // Create connection to previous node
    const connection: ParsedFlowItem = {
      id: generateId(),
      type: 'connection',
      text: '',
      sourceId: lastNodeId,
      targetId: nodeId,
      condition: type === 'decision' ? conditionText : undefined,
    };
    
    items.push(node);
    items.push(connection);
    
    // Update lastNodeId for next iteration
    lastNodeId = nodeId;
    
    // For the last segment, add an end node
    if (index === segments.length - 1) {
      const endNodeId = generateId();
      const endNode: ParsedFlowItem = {
        id: endNodeId,
        type: 'end',
        text: 'End',
      };
      
      const finalConnection: ParsedFlowItem = {
        id: generateId(),
        type: 'connection',
        text: '',
        sourceId: lastNodeId,
        targetId: endNodeId,
      };
      
      items.push(endNode);
      items.push(finalConnection);
    }
  });
  
  // Store in cache for future reference
  parsedSegmentsCache.set(normalizedText, items);
  
  // Clear cache if it gets too large
  if (parsedSegmentsCache.size > 100) {
    const keysToDelete = Array.from(parsedSegmentsCache.keys()).slice(0, 50);
    keysToDelete.forEach(key => parsedSegmentsCache.delete(key));
  }
  
  return items;
};

// Convert parsed items to ReactFlow nodes and edges
export const convertToFlowchart = (items: ParsedFlowItem[]): FlowchartData => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  
  // Process nodes first
  items.filter(item => item.type !== 'connection').forEach((item, index) => {
    let nodeType = 'default';
    let style = {};
    
    switch (item.type) {
      case 'start':
        nodeType = 'input';
        style = { 
          background: '#d4f0f0', 
          border: '1px solid #2a9d8f',
          borderRadius: '30px',
          width: 150,
          padding: 10
        };
        break;
      case 'end':
        nodeType = 'output';
        style = { 
          background: '#ffcfd2',
          border: '1px solid #e76f51',
          borderRadius: '30px',
          width: 150,
          padding: 10
        };
        break;
      case 'decision':
        nodeType = 'default';
        style = { 
          background: '#fbe8a6', 
          border: '1px solid #f4a261',
          borderRadius: '4px',
          width: 200,
          padding: 10,
          transform: 'rotate(45deg)',
          transformOrigin: 'center',
        };
        break;
      default:
        style = { 
          background: '#e9ecef', 
          border: '1px solid #adb5bd',
          borderRadius: '4px',
          width: 200,
          padding: 10
        };
    }
    
    // Create a node with capitalized text
    const capitalizedText = capitalizeFirstLetter(item.text);
    
    nodes.push({
      id: item.id,
      type: nodeType,
      position: { x: 250, y: 100 + index * 100 },
      data: { 
        label: capitalizedText,
        ...(item.type === 'decision' ? { style: { transform: 'rotate(-45deg)' } } : {})
      },
      style
    });
  });
  
  // Process connections (edges)
  items.filter(item => item.type === 'connection').forEach(item => {
    if (item.sourceId && item.targetId) {
      edges.push({
        id: item.id,
        source: item.sourceId,
        target: item.targetId,
        label: item.condition || '',
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#555' }
      });
    }
  });
  
  return { nodes, edges };
};

// Layout optimization - simple automatic layout with smooth animations
export const optimizeLayout = (flowData: FlowchartData): FlowchartData => {
  const { nodes, edges } = flowData;
  const positionMap = new Map<string, { level: number; order: number }>();
  
  // First pass: Assign levels to each node
  const startNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));
  let currentLevel = 0;
  let nodesToProcess = [...startNodes];
  
  while (nodesToProcess.length > 0) {
    const nextLevelNodes: Node[] = [];
    
    nodesToProcess.forEach((node, index) => {
      positionMap.set(node.id, { level: currentLevel, order: index });
      
      // Find all nodes this node connects to
      const childEdges = edges.filter(edge => edge.source === node.id);
      const childNodes = childEdges.map(edge => nodes.find(n => n.id === edge.target)!).filter(Boolean);
      
      nextLevelNodes.push(...childNodes);
    });
    
    currentLevel++;
    nodesToProcess = nextLevelNodes;
  }
  
  // Second pass: Layout nodes
  const levelWidth = 300;
  const levelHeight = 150;
  const nodeWidth = 200;
  const startX = 50;
  const startY = 50;
  
  // Count nodes per level
  const levelsMap = new Map<number, number>();
  positionMap.forEach(({ level }) => {
    levelsMap.set(level, (levelsMap.get(level) || 0) + 1);
  });
  
  // Calculate node positions
  const updatedNodes = nodes.map(node => {
    const position = positionMap.get(node.id);
    
    if (!position) {
      return node;
    }
    
    const { level, order } = position;
    const nodesInLevel = levelsMap.get(level) || 1;
    const levelSpace = Math.max((nodesInLevel * nodeWidth), levelWidth);
    
    const x = startX + (levelSpace / (nodesInLevel + 1)) * (order + 1);
    const y = startY + level * levelHeight;
    
    return {
      ...node,
      position: { x, y }
    };
  });
  
  return { nodes: updatedNodes, edges };
}; 